<template>
  <IconField>
    <slot name="inline-prefix"></slot>

    <InputText
      fluid
      v-model="model"
      :feedback="props.feedback"
      :id="fieldIdComputed"
      :disabled="props.disabled"
      :class="classComputed"
      :placeholder="props.placeholder"
      :auto-complete="props.autoComplete"
      :auto-focus="props.autoFocus"
      :type="type"
      @update:modelValue="onChange"
      @keyup="onKeyup"
    ></InputText>

    <InputIcon v-if="props.toggleMask">
      <Button :disabled="props.disabled" variant="outlined" aria-controls="password" @click="onToggleMaskClick">
        <OutlinedIcon :icon="visibilityIconComputed" class="text-lg" />
      </Button>
    </InputIcon>

    <slot name="inline-sufix"></slot>
  </IconField>
</template>

<script setup lang="ts">
import { IValidatedForm } from "@common/hooks";
import { InputTextTypeType } from "@common/types/ui/InputTextTypeType";
import { Button } from "@common/ui/atoms/Buttons/Button";
import { InputText } from "@common/ui/atoms/Form/InputText";
import { OutlinedIcon } from "@common/ui/atoms/Icons/OutlinedIcon";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { computed, inject, ref } from "vue";

// Props
const props = defineProps({
  autoComplete: {
    type: String,
    required: false,
  },
  autoFocus: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
  feedback: {
    type: Boolean,
    required: false,
    default: false,
  },
  fluid: {
    type: Boolean,
    required: false,
  },
  id: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    required: false,
  },
  toggleMask: {
    type: Boolean,
    required: false,
  },
});

// Emits
const emit = defineEmits<{
  (e: "change", value: string | undefined): void;
  (e: "enter"): void;
  (e: "keyup", event: KeyboardEvent): void;
}>();

// Models
const model = defineModel<string>();

// Variables
const forId = inject<string | null>("$for", null);
const target = inject<string | null>("$target", null);
const validatedForm = inject<IValidatedForm | null>("$validatedForm", null);
const type = ref<InputTextTypeType>("password");

// Computed
const classComputed = computed(() => {
  const result = [];

  if (target && validatedForm?.validator.value.errors[target]) {
    result.push("p-invalid");
  }

  return result;
});

const fieldIdComputed = computed(() => {
  return props.id ?? forId ?? undefined;
});

const visibilityIconComputed = computed(() => {
  return type.value === "password" ? "visibility" : "visibility_off";
});

// Methods
const onChange = (value: string | undefined) => {
  validateField();

  emit("change", value);
};

const onKeyup = (e: KeyboardEvent) => {
  validateField();

  emit("keyup", e);

  if (e.key === "Enter") {
    emit("enter");
  }
};

const onToggleMaskClick = () => {
  type.value = type.value === "password" ? "text" : "password";
};

const validateField = () => {
  if (target && validatedForm) {
    validatedForm.validateField(target);
  }
};
</script>
